import { Link } from "react-router-dom";
import { Phone, Mail, CalendarClock, MessageCircle, QrCode } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { FadeIn } from "@/components/Reveal";
import ConsultationForm from "@/components/ConsultationForm";

const Contact = () => {
  usePageMeta(
    "Schedule a Consultation | Holistic Healing Psychotherapy, Inc.",
    "Reach out for couples or individual therapy in California. A brief consultation is a low-pressure way to see whether we're a good fit."
  );

  return (
    <div data-testid="contact-page" className="bg-cream-50">
      <section className="pt-14 pb-20 lg:pt-24 lg:pb-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <FadeIn>
              <p className="eyebrow mb-6" data-testid="contact-eyebrow">Contact · Consultation</p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] text-ink" data-testid="contact-headline">
                Let's talk about what you're{" "}
                <em className="text-forest-700">going through</em>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-sage leading-relaxed" data-testid="contact-subcopy">
                Reaching out for therapy can feel like a big step. A free 20-minute consultation gives us an
                opportunity to talk about what is bringing you in, what you are hoping to change and whether I may be
                the right therapist for you.
              </p>
            </FadeIn>

            <FadeIn delay={0.15}>
              <div className="mt-10 space-y-4" data-testid="contact-methods">
                <a
                  href="tel:+16573339466"
                  data-testid="contact-phone-link"
                  className="flex items-center gap-4 rounded-xl border border-forest-800/10 bg-cream-100 px-6 py-5 transition-colors duration-300 hover:border-gold-500/50"
                >
                  <Phone size={20} className="text-gold-500 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-sage">Call or Text</p>
                    <p className="font-serif text-xl text-forest-800">(657) 333-9466</p>
                  </div>
                </a>
                <a
                  href="mailto:bqholistichealing@gmail.com"
                  data-testid="contact-email-link"
                  className="flex items-center gap-4 rounded-xl border border-forest-800/10 bg-cream-100 px-6 py-5 transition-colors duration-300 hover:border-gold-500/50"
                >
                  <Mail size={20} className="text-gold-500 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-sage">Email</p>
                    <p className="font-serif text-xl text-forest-800 break-all">bqholistichealing@gmail.com</p>
                  </div>
                </a>
                <a
                  href="https://wa.me/16573339466"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-link"
                  className="flex items-center gap-4 rounded-xl border border-forest-800/10 bg-cream-100 px-6 py-5 transition-colors duration-300 hover:border-gold-500/50"
                >
                  <MessageCircle size={20} className="text-gold-500 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-sage">WhatsApp</p>
                    <p className="font-serif text-xl text-forest-800">Message (657) 333-9466</p>
                  </div>
                </a>
                <Link
                  to="/card"
                  data-testid="contact-business-card-link"
                  className="flex items-center gap-4 rounded-xl border border-forest-800/10 bg-cream-100 px-6 py-5 transition-colors duration-300 hover:border-gold-500/50"
                >
                  <QrCode size={20} className="text-gold-500 shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-[0.2em] text-sage">Virtual Business Card</p>
                    <p className="font-serif text-xl text-forest-800">Scan to Save My Contact</p>
                  </div>
                  <img
                    src="/business-card/qr-contact.png"
                    alt="QR code that saves the practice contact to a phone"
                    className="w-12 h-12 rounded-md shrink-0"
                  />
                </Link>
                <a
                  href="https://therapyfinder.com/therapist/breeza-y-quezada-licensed-clinical-social-worker-long-beach-ca"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-scheduler-link"
                  className="flex items-center gap-4 rounded-xl border border-gold-500/40 bg-forest-900 px-6 py-5 transition-colors duration-300 hover:bg-forest-950"
                >
                  <CalendarClock size={20} className="text-gold-400 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-cream-50/60">Prefer to book directly?</p>
                    <p className="font-serif text-xl text-cream-50">Book Online — Free 20-Minute Consultation</p>
                  </div>
                </a>
                <p className="text-xs text-sage/80 pt-1" data-testid="contact-location-note">
                  Based in Long Beach, California · Serving clients statewide via secure telehealth
                </p>
              </div>
              <p className="mt-6 text-xs text-sage/80 leading-relaxed" data-testid="contact-privacy-note">
                This form is for scheduling and general inquiries. Please do not include detailed clinical information.
                If you are in crisis, call or text 988 or dial 911.
              </p>
              <div className="mt-6 rounded-xl border border-forest-800/10 bg-cream-100 px-6 py-5" data-testid="contact-inperson-note">
                <p className="text-xs uppercase tracking-[0.2em] text-sage mb-2">Visiting In Person?</p>
                <p className="text-sm text-ink/80 leading-relaxed">
                  3711 Long Beach Blvd, Suite 645, Long Beach, CA 90807. Take the elevator to the 6th floor and
                  proceed to the right down the hallway — Suite #645, the waiting area, will be on your left. Take a
                  seat and you will be called in at your scheduled appointment time. In-office appointments are
                  available on Saturdays only, and in-office rates may vary.
                </p>
              </div>
            </FadeIn>
          </div>

          <div className="lg:col-span-7">
            <FadeIn delay={0.2}>
              <div className="rounded-2xl border border-forest-800/10 bg-white/60 backdrop-blur p-7 sm:p-10 shadow-[0_24px_60px_rgba(18,55,42,0.08)]">
                <h2 className="font-serif text-2xl sm:text-3xl text-forest-800 mb-2" data-testid="contact-form-title">
                  Request a Consultation
                </h2>
                <p className="text-sm text-sage mb-8">Every request is read personally. Expect a reply within one to two business days.</p>
                <ConsultationForm />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
